import * as React from "react";
import { CommonHelper } from "../../../../../../../react-core/Helpers/CommonHelper";
import { DepositMethod } from "../../../../../../../Wrap/Constants/Enums/Domain/DepositMethod";
import { SubAccountType } from "../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { RestrictionTypeEnum } from "../../../../../../../Wrap/Constants/Enums/RestrictionTypeEnum";
import { BankAccount } from "../../../../../../../Wrap/Models/Domains/BankAccount/BankAccount";
import { BankAccountInstruction } from "../../../../../../../Wrap/Models/Domains/Instructions/BankAccountInstruction";
import { ClientDetails } from "../../../../../../../Wrap/Models/Models/Processes/Quote/ClientDetails";
import { User } from "../../../../../../../Wrap/Models/Models/User/User";
import { CheckboxGroup } from "../../../../../../Shared/CheckboxGroup/CheckboxGroup";
import { RestrictionHelper } from "../../../../../../Shared/Common/RestrictionHelper";
import { GENERIC_RESTRICTIONS_MESSAGE } from "../../../../../../Shared/Common/UserMessages";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { WizardLinter, EWizardLinterStatus } from "../../../../../../Shared/Process/Process/WizardLinter";
import { getQueryParameters } from "../../../../../../Shared/Wizards/Entities/QueryParametersHelper";
import { Action } from "../../../../../Actions/Action";
import { UserStore } from "../../../../../Stroes/Variable";
import { SectionHeader } from "../../../AddClient/Components/SharedComponents";
import { EWizardLinterHelper } from "../../../NewBusinessWizard/Resource/EWizardLinterHelper";
import { isTaxRelifAvailable } from "../../../NewQuoteWizard/Components/Shared/Utils";
import { EmployerInfoIcon, PaymentType, processTypeEnum } from "../../Resource/Contents";
import { RestrictionStore } from "../../Restrictions/Variable";
import { SinglePaymentDetail } from "./SinglePaymentDetail";
import { SIPPPaymentInCheckboxGroup } from "../SIPPPaymentInCheckboxGroup";
import { SubAccountTypeHelper } from "../../Helpers/SubAccountTypeHelper";

interface ISinglePaymentProps extends IProcessComponentProps {
    clientInfo: ClientDetails;
    checkBoxOption: Array<PaymentType>;
    subAccountType: SubAccountType;
    hasPaymentMethod: boolean;
    bankAccounts?: BankAccount[];
    bankAccountInstructions?: BankAccountInstruction[];
    processType: processTypeEnum;
    excludeWrapCash?: boolean;
    HasExistingSIPP?: boolean;
    onSaveNewBankAccountInstruction?: (BankAccountInstruction: BankAccountInstruction) => void;
    onRemoveBankAccountInstruction?: (BankAccountInstruction: BankAccountInstruction) => void;
    inUseBankAccountInstruction?: string[];
    IsDisqualifyingPensionCredits?: boolean;
}

interface ISinglePaymentState {
    addNewBankAccount: boolean;
    userInfo: User;
    isTaxRelief: boolean;
}

export class SinglePayment extends ProcessComponent<ISinglePaymentProps, ISinglePaymentState> {
    constructor(props: any) {
        super(props);
        this.state = {
            userInfo: UserStore.getUserInfo(),
            addNewBankAccount: false,
            isTaxRelief: true,
        };
    }

    async componentDidMount() {
        this.setState({isTaxRelief : await isTaxRelifAvailable(this.props.clientInfo?.DateOfBirth, new Date()) && (this.props.subAccountType === SubAccountType.Npr || this.props.subAccountType === SubAccountType.JuniorSipp)});
    }

    public isValid(): boolean {
        let isValid = super.isValid();
        if (RestrictionHelper.shouldHandleRestriction(this.props.processType)) {
            isValid = isValid && !RestrictionStore.depositDisabled();
        }
        return isValid;
    }

    private getKey = (key: PaymentType) => {
        switch (key) {
            case PaymentType.Client:
                return "ClientSinglePayment";
            case PaymentType.ThirdParty:
                return "ThirdPartySinglePayment";
            case PaymentType.Employer:
                return "EmployerSinglePayment";
            case PaymentType.EmployerSalaryDeduction:
                return "EmployerSalaryDeductionSinglePayment";
        }
    };

    private getOptions = () => {
        const { checkBoxOption, data, onChange, processType } = this.props;
        let isRestrictionActive = false;
        if (RestrictionHelper.shouldHandleRestriction(processType)) {
            isRestrictionActive = RestrictionStore.depositDisabled();
        }
        const isNone = checkBoxOption.map((type) => data[this.getKey(type)] == null).every((ele) => ele == true);
        const empty = {};
        checkBoxOption.forEach((type) => {
            empty[this.getKey(type)] = null;
        });
        const options = [
            {
                label: "None",
                value: isNone,
                disabled: isRestrictionActive || this.props.IsDisqualifyingPensionCredits,
                onChange: (v) => onChange(empty),
            },
        ];

        checkBoxOption.forEach((type) => {
            options.push({
                label: type,
                value: data[this.getKey(type)] != null,
                disabled:
                    ((this.props.processType == processTypeEnum.NewBusinessExistingClient ||
                        this.props.processType == processTypeEnum.NewQuoteExistingClient) &&
                        this.props.HasExistingSIPP == true) ||
                    isRestrictionActive ||
                    this.props.IsDisqualifyingPensionCredits,

                onChange: (v) => {
                    if (v) {
                        const initial = {
                            TaxRelief: this.state.isTaxRelief && SubAccountTypeHelper.isOnPlatformPension(this.props.subAccountType),
                            PaymentMethod: DepositMethod.DirectCredit,
                        };
                        this.onDataChange(onChange, initial, data, this.getKey(type));
                    } else {
                        onChange({
                            [this.getKey(type)]: null,
                        });
                    }
                },
            });
        });

        return options;
    };

    private getProps = (key) => {
        const {
            data,
            onChange,
            subAccountType,
            clientInfo,
            bankAccounts,
            bankAccountInstructions,
            hasPaymentMethod,
            processType,
            excludeWrapCash,
            isReview,
            onSaveNewBankAccountInstruction,
            onRemoveBankAccountInstruction,
            inUseBankAccountInstruction,
        } = this.props;
        return {
            clientInfo: clientInfo,
            type: key,
            data: data[this.getKey(key)],
            onChange: (changes) => this.onDataChange(onChange, changes, data, this.getKey(key)),
            onRef: (ref) => this.appendChild(ref),
            subAccountType: subAccountType,
            bankAccounts: bankAccounts,
            bankAccountInstructions: bankAccountInstructions,
            hasPaymentMethod: hasPaymentMethod,
            processType: processType,
            excludeWrapCash: excludeWrapCash,
            isReview: isReview,
            onSaveNewBankAccountInstruction: onSaveNewBankAccountInstruction,
            onRemoveBankAccountInstruction: onRemoveBankAccountInstruction,
            showErrorMsgPopUp: this.props.showErrorMsgPopUp,
            inUseBankAccountInstruction: inUseBankAccountInstruction,
        };
    };

    public render(): JSX.Element {
        const { data, subAccountType, processType, IsDisqualifyingPensionCredits } = this.props;
        if (IsDisqualifyingPensionCredits) {
            data.ClientSinglePayment = null;
            data.ThirdPartySinglePayment = null;
            data.EmployerSalaryDeductionSinglePayment = null;
            data.EmployerSinglePayment = null;
        }
        let isRestrictionActive = false;
        if (RestrictionHelper.shouldHandleRestriction(processType)) {
            isRestrictionActive = RestrictionStore.depositDisabled();
        }
        let restrictedMessage = "An account with suspended or restricted activities cannot receive payments in";

        if (this.state.userInfo != null && this.state.userInfo.IsAdviser) {
            restrictedMessage = GENERIC_RESTRICTIONS_MESSAGE;
        }
        var options = this.getOptions()
        var hasEmployerOption = options.find(x=>x.label == "Employer")
        return (
            <WizardLinter
                status={EWizardLinterStatus.PleaseCheck}
                enabled={EWizardLinterHelper.shouldDisplay(subAccountType)}
            >
                {!hasEmployerOption && SectionHeader("Single payment")}
                {hasEmployerOption && SectionHeader("Single payment",EmployerInfoIcon)}
                {isRestrictionActive && <Alert alertType={AlertType.Error} message={restrictedMessage} />}
                {([processTypeEnum.NewBusiness, processTypeEnum.NewBusinessExistingClient, 
                    processTypeEnum.Quote, processTypeEnum.SinglePaymentTransfer, 
                    processTypeEnum.topUpWithITFLS, processTypeEnum.WhatIfTool]
                        .includes(this.props.processType)) &&
                    (this.props.subAccountType == SubAccountType.Npr || this.props.subAccountType == SubAccountType.JuniorSipp) ?
                    (<SIPPPaymentInCheckboxGroup type="150" wrapCss="square-checkbox" options={options} />) :
                    (<CheckboxGroup type="150" wrapCss="square-checkbox" options={this.getOptions()} />)}
                {data.ClientSinglePayment && <SinglePaymentDetail {...this.getProps(PaymentType.Client)} />}
                {data.ThirdPartySinglePayment && <SinglePaymentDetail {...this.getProps(PaymentType.ThirdParty)} />}
                {data.EmployerSalaryDeductionSinglePayment && <SinglePaymentDetail {...this.getProps(PaymentType.EmployerSalaryDeduction)} />}
                {data.EmployerSinglePayment && <SinglePaymentDetail {...this.getProps(PaymentType.Employer)} />}
            </WizardLinter>
        );
    }
}
